/**
 * swUpdate.js — push the registered service worker(s) to check for a new
 * version and wait (bounded) for the fresh worker to reach 'activated'.
 *
 * Why this exists: src/sw.js calls skipWaiting() but deliberately NOT
 * clientsClaim() (reclaiming open tabs mid-deploy would 404 their next lazy
 * route chunk — see the comment there). The consequence is that a plain
 * window.location.reload() races the new worker's install: until it reaches
 * 'activated', the OLD worker still handles the navigation and serves the OLD
 * precached /200.html, so the reload lands right back on the stale bundle.
 * Callers that reload in order to pick up a new deploy must first wait for the
 * new worker via this helper, or the reload accomplishes nothing.
 *
 * Never rejects. Resolves true when a fresh worker reached 'activated' within
 * the cap, false otherwise (no SW support, no registration, no new worker
 * found, worker went redundant, timeout, or any error). Callers should reload
 * either way — false just means the reload is not guaranteed to adopt.
 */

// Upper bound on the wait. Install requires fetching the whole precache
// manifest, so on a cold cache this can legitimately take a few seconds; but
// the user has just asked for a reload, so we will not hold the page hostage —
// on expiry we give up waiting and let the caller reload anyway.
export const SW_ACTIVATION_TIMEOUT_MS = 5000;

const isSupported = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.serviceWorker &&
  typeof navigator.serviceWorker.getRegistrations === 'function';

/**
 * Resolve true when `reg`'s in-flight worker (installing or waiting) reaches
 * 'activated'; false when there is none or it goes redundant. Never rejects.
 * The bounded wait lives in the caller — a dangling statechange listener is
 * harmless because the page reloads immediately afterwards.
 */
const waitForActivation = (reg) =>
  new Promise((resolve) => {
    const worker = reg.installing || reg.waiting;
    if (!worker) {
      resolve(false);
      return;
    }
    if (worker.state === 'activated') {
      resolve(true);
      return;
    }
    const onStateChange = () => {
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', onStateChange);
        resolve(true);
      } else if (worker.state === 'redundant') {
        worker.removeEventListener('statechange', onStateChange);
        resolve(false);
      }
    };
    worker.addEventListener('statechange', onStateChange);
  });

/**
 * Fire-and-forget update nudge. Browsers do NOT check for a new service
 * worker on every navigation — Chrome only soft-updates when the registration
 * is "stale" (>24 h since its last check). A tab that checked this morning
 * will therefore reload straight past a new deploy all day (verified live,
 * 2026-08-13: 40 s + a navigation produced no install; one explicit update()
 * installed and activated the new worker in seconds). Call this at boot and
 * before prompting so adoption never depends on that 24-hour clock.
 */
export function nudgeServiceWorkerUpdate() {
  if (!isSupported()) return;
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => regs.forEach((r) => Promise.resolve(r.update()).catch(() => {})))
    .catch(() => {});
}

/**
 * Trigger an update check on every registration and wait (up to `timeoutMs`)
 * for a newly-installed worker to activate.
 *
 * @param {number} [timeoutMs]
 * @returns {Promise<boolean>} true iff a fresh worker activated in time
 */
export function updateServiceWorkerAndWait(timeoutMs = SW_ACTIVATION_TIMEOUT_MS) {
  if (!isSupported()) {
    return Promise.resolve(false);
  }
  const attempt = (async () => {
    const regs = await navigator.serviceWorker.getRegistrations();
    if (!regs || regs.length === 0) {
      return false;
    }
    // update() rejections (offline, server error) must not sink the wait —
    // a worker may already be installing from the browser's own check.
    await Promise.all(regs.map((r) => Promise.resolve(r.update()).catch(() => {})));
    const activated = await Promise.all(regs.map((r) => waitForActivation(r)));
    return activated.some(Boolean);
  })();
  const cap = new Promise((resolve) => {
    setTimeout(() => resolve(false), timeoutMs);
  });
  return Promise.race([attempt, cap]).catch(() => false);
}
